import { CommonModule } from '@angular/common';
import { Component, Inject, input, Input, model } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Clipboard } from '@angular/cdk/clipboard';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  CrmDependencies,
  CrmInjectionToken,
  CrmObjectDependencies,
  LoadingStatus,
  ObjectType,
  TranslateForCrmObjectPipe,
} from '@galaxy/crm/static';
import { MatDialog } from '@angular/material/dialog';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { map, of, shareReplay, switchMap } from 'rxjs';
import { DynamicProfileHeaderCardsComponent } from './profile-header-cards/dynamic-profile-header-cards.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { CrmObjectInterface } from '@vendasta/crm';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';

@Component({
  selector: 'crm-profile-header',
  templateUrl: './profile-header.component.html',
  styleUrls: ['./profile-header.component.scss'],
  imports: [
    CommonModule,
    TranslateForCrmObjectPipe,
    MatIconModule,
    MatMenuModule,
    GalaxyBadgeModule,
    DynamicProfileHeaderCardsComponent,
    GalaxyTooltipModule,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
  ],
})
export class CrmProfileHeaderComponent {
  objectType = input<ObjectType>();
  loadingStatus = model<LoadingStatus>(LoadingStatus.LOADING);
  @Input() name = '';
  @Input() email = '';
  @Input() phone = '';
  @Input() website = '';
  @Input() subtitle: string | null = '';
  @Input() showMeetingIcon: boolean;
  @Input() contactId = '';
  @Input() onClickOpenConversation?: () => void | Promise<void>;
  @Input() onClickEdit?: () => void | Promise<void>;
  @Input() crmObject?: CrmObjectInterface;

  calendarImgUrl = this.getCalendarImageSrc();

  private readonly objectType$ = toObservable(this.objectType);
  private readonly profileHeaders$ = this.objectType$.pipe(
    map((ot) => this.getDependeciesByObject(ot)),
    switchMap((dependencies) => dependencies.profileHeader$ ?? of([])),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );
  protected readonly topProfileHeaders$ = this.profileHeaders$.pipe(
    map((headers) => headers.filter((header) => !header.position || header.position === 'top')),
  );
  protected readonly bottomProfileHeaders$ = this.profileHeaders$.pipe(
    map((headers) => headers.filter((header) => header.position === 'bottom')),
  );

  constructor(
    private clipboard: Clipboard,
    private snackbarService: SnackbarService,
    public dialog: MatDialog,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
  ) {}

  copyToClipboard(name: string): void {
    this.clipboard.copy(name);
    this.snackbarService.openSuccessSnack('COMMON.SNACKBAR_MESSAGE.COPY_SUCCESS', { action: 'Close' });
  }
  openMeetingEventListDialog() {
    if (this.config.meetingAction) {
      this.config.meetingAction(
        this.contactId,
        this.config.currentUserId$,
        this.config.namespace$,
        this.config.parentNamespace$,
      );
    }
  }

  getCalendarImageSrc(): string {
    return `https://storage.googleapis.com/meeting-scheduler-prod-public-images/calendar.svg`;
  }

  private getDependeciesByObject(objectType?: ObjectType): CrmObjectDependencies {
    switch (objectType) {
      case 'Contact':
        return this.config?.contact ?? {};
      case 'Company':
        return this.config?.company ?? {};
      case 'Opportunity':
        return this.config?.opportunity ?? {};
      default:
        return {};
    }
  }

  isLoading(): boolean {
    return this.loadingStatus() === LoadingStatus.LOADING;
  }
}
